import { Form, InputNumber, Modal, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import { loanDrawdownsAddAction } from '~Loans/actions';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { IDictionary } from '~utilities/IDictionary';

export default function DrawdownAddModal(props: {
    isOpen: boolean;
    loan: ILoan;
    onCancel: () => void;
}): ReactElement {
    const { isOpen, loan, onCancel } = props;

    const [ dateRequestReceived, setDateRequestReceived ] = useState<string>(null);
    const [ amountRequested, setAmountRequested ] = useState<number>(null);
    const [ errors, setErrors] = useState<IDictionary<string>>({});

    const dispatch: Dispatch = useDispatch();

    const validateDateRequestReceived: () => boolean = useCallback(() => {
        let error: string = null;

        if (!dateRequestReceived) {
            error = 'Please enter the date request received';
        } else if (dayjs(dateRequestReceived).isSameOrAfter(dayjs())) {
            error = 'Date request received must be in the past';
        }

        setErrors((previousErrors: IDictionary<string>) => ({
            ...previousErrors,
            dateRequestReceived: error,
        }));

        return !error;
    }, [dateRequestReceived]);

    const validateAmountRequested: () => boolean = useCallback(() => {
        let error: string = null;

        if (amountRequested === null) {
            error = 'Please enter the amount requested';
        } else if (amountRequested <= 0) {
            error = 'Amount requested must be greater than $0';
        }

        setErrors((previousErrors: IDictionary<string>) => ({
            ...previousErrors,
            amountRequested: error,
        }));

        return !error;
    }, [amountRequested]);

    const disabledTime: (time: Dayjs) => boolean = useCallback((time: Dayjs) => {
        return time && time.isAfter(dayjs(), 'second');
    }, []);

    const onClickOk: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateDateRequestReceived() && valid;
        valid = validateAmountRequested() && valid;

        if (!valid) {
            return;
        }

        dispatch(loanDrawdownsAddAction(loan.uuid, amountRequested, dateRequestReceived));
        onCancel();
    }, [
        amountRequested,
        dateRequestReceived,
        dispatch,
        loan,
        onCancel,
        validateAmountRequested,
        validateDateRequestReceived,
    ]);

    const onChangeDateRequestReceived: (date: Dayjs) => void = useCallback((date: Dayjs) => {
        setDateRequestReceived(date ? dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(dateRequestReceived || undefined).format('HH:mm')}`).format() : null);
    }, [dateRequestReceived]);

    const onChangeTimeRequestReceived: (time: Dayjs) => void = useCallback((time: Dayjs) => {
        setDateRequestReceived(time ? dayjs(`${dayjs(dateRequestReceived || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format() : null);
    }, [dateRequestReceived]);

    const onChangeAmountRequested: (amountRequested: number) => void = useCallback((amountRequested: number) => {
        setAmountRequested(amountRequested);
    }, []);

    return (
        <Modal
            okText='Create'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Create Drawdown'
            wrapClassName='loan-drawdown-add-modal'
            width={640}
        >
            <Form.Item
                label='Date Request Received'
                className='date-request-received'
                help={errors.dateRequestReceived}
                validateStatus={errors.dateRequestReceived && 'error'}
            >
                <Space>
                    <DatePicker
                        className='date-request-received-pick-date'
                        disabledDate={disabledTime}
                        onChange={onChangeDateRequestReceived}
                        format='DD/MM/YYYY'
                        value={dateRequestReceived ? dayjs(dateRequestReceived) : null}
                    />
                    <TimePicker
                        className='date-request-received-pick-time'
                        disabledDate={disabledTime}
                        onChange={onChangeTimeRequestReceived}
                        value={dateRequestReceived ? dayjs(dateRequestReceived) : null}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
            <Form.Item
                label='Amount Requested'
                help={errors.amountRequested}
                validateStatus={errors.amountRequested && 'error'}
            >
                <InputNumber addonBefore='$' min={0} onChange={onChangeAmountRequested} type='number' value={amountRequested} />
            </Form.Item>
        </Modal>
    );
}